import { format } from 'date-fns';
import { loadStripe } from '@stripe/stripe-js';
import config from '../../config';
import { formatMoney } from '../../util/currency';
import { parseInsuranceApiError } from '../../util/landownerInsuranceEnrollmentErrors';
import { types as sdkTypes } from '../../util/sdkLoader';
import { getInsuranceDetails, INSURANCE_STATUSES } from '../../util/user';

const { Money } = sdkTypes;
const DEFAULT_LANDOWNER_INSURANCE_PRICE = 10000;

export const getLandownerInsuranceBasePrice = () => {
  const configuredPrice = Number.parseInt(process.env.REACT_APP_LANDOWNER_INSURANCE_PRICE, 10);

  return Number.isFinite(configuredPrice) ? configuredPrice : DEFAULT_LANDOWNER_INSURANCE_PRICE;
};

export const PROMO_CODE_ERROR_MESSAGE_IDS = {
  'stripe-promo-code-not-found': 'PromoCode.invalidPromoCode',
  'stripe-promo-code-not-valid-for-product': 'PromoCode.invalidPromoCode',
};

export const stripePromise = loadStripe(config.stripe.publishableKey);
export const INSURANCE_STATUS_POLL_INTERVAL_MS = 2000;
export const INSURANCE_STATUS_MAX_REFRESH_ATTEMPTS = 5;
export const PAYMENT_METHOD_DEFAULT = 'defaultCard';
export const PAYMENT_METHOD_REPLACE = 'replaceCard';
export const INSURANCE_REFRESH_STATUSES = [
  INSURANCE_STATUSES.ACTIVE,
  INSURANCE_STATUSES.PENDING,
  INSURANCE_STATUSES.CANCELED,
];

export const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

export const formatInsurancePrice = (intl, amount) =>
  formatMoney(intl, new Money(amount, config.currency));

export const formatCoverageDate = timestamp => {
  if (!timestamp) {
    return null;
  }

  const numericTimestamp = Number(timestamp);

  if (!Number.isFinite(numericTimestamp)) {
    return null;
  }

  const timestampInMilliseconds =
    numericTimestamp > 1000000000000 ? numericTimestamp : numericTimestamp * 1000;

  return format(new Date(timestampInMilliseconds), 'MMMM DD, YYYY');
};

export const getInsuranceCoverageStartDate = user => {
  const insuranceDetails = getInsuranceDetails(user);
  const subscription = insuranceDetails?.subscription || {};

  return (
    subscription.startDate ||
    subscription.currentPeriodStart ||
    subscription.created ||
    insuranceDetails?.enrolledAt ||
    insuranceDetails?.coverageStartDate ||
    null
  );
};

const formatCardBrand = brand => {
  if (!brand) {
    return 'Card';
  }

  return brand.charAt(0).toUpperCase() + brand.slice(1);
};

export const getPaymentMethodLabel = paymentMethod => {
  const card = paymentMethod?.card || paymentMethod;
  const last4Digits = card?.last4Digits || card?.last4;

  return last4Digits ? `${formatCardBrand(card.brand)} ••••${last4Digits}` : null;
};

export const getInsurancePaymentMethodLabel = user => {
  const insuranceDetails = getInsuranceDetails(user);
  const paymentMethod =
    insuranceDetails?.paymentMethod || insuranceDetails?.subscription?.paymentMethod;

  return getPaymentMethodLabel(paymentMethod);
};

export const getDefaultPaymentMethod = user => user?.stripeCustomer?.defaultPaymentMethod || null;

export const getDefaultPaymentMethodCard = user =>
  getDefaultPaymentMethod(user)?.attributes?.card || null;

export const getDefaultStripePaymentMethodId = user =>
  getDefaultPaymentMethod(user)?.attributes?.stripePaymentMethodId || null;

export const getDefaultPaymentMethodLabel = user => {
  const card = getDefaultPaymentMethodCard(user);

  return getPaymentMethodLabel(card);
};

export const getPaymentUpdateBillingDetails = user => {
  const profile = user?.attributes?.profile || {};
  const email = user?.attributes?.email;
  const billingDetails = {};

  if (email) {
    billingDetails.email = email;
  }

  if (profile.firstName && profile.lastName) {
    billingDetails.name = `${profile.firstName} ${profile.lastName}`;
  } else if (profile.displayName) {
    billingDetails.name = profile.displayName;
  }

  return billingDetails;
};

export const hasInsurancePaymentIssue = user => {
  const insuranceDetails = getInsuranceDetails(user);
  const subscription = insuranceDetails?.subscription || {};
  const paymentStatus =
    subscription.paymentStatus || subscription.latestInvoiceStatus || subscription.invoiceStatus;
  const subscriptionStatus = subscription.status;

  return (
    subscription.renewalPaymentActionRequired ||
    subscription.paymentActionRequired ||
    ['past_due', 'unpaid', 'incomplete'].includes(subscriptionStatus) ||
    [
      'past_due',
      'unpaid',
      'payment_failed',
      'requires_action',
      'requires_payment_method',
      'open',
    ].includes(paymentStatus)
  );
};

export const getPaymentMethodUpdateUserMessage = (error, intl) => {
  const apiError = parseInsuranceApiError(error);

  if (
    [
      'stripe-subscription-not-found',
      'stripe-customer-id-not-found',
      'stripe-payment-method-customer-mismatch',
    ].includes(apiError?.message)
  ) {
    return intl.formatMessage({ id: 'LandownerInsurance.updatePaymentSupportError' });
  }

  return intl.formatMessage({ id: 'LandownerInsurance.updatePaymentError' });
};
